import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";

import React, { useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";
import { isValidIpV4 } from "utils/network-validation";
import Loading from "../loading";

const parseIpCsv = (csvContent) => {
  const lines = csvContent.split(/\r?\n/);
  const result = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const lowerLine = line.toLowerCase();
    if (lowerLine.startsWith("ip address") || lowerLine.startsWith("ipaddress")) {
      continue;
    }

    const parts = line.includes(",")
      ? line.split(",").map(part => part.trim())
      : line.split(/\s+/);
    const ipWithSubnet = parts[0] || "";
    const description = line.includes(",")
      ? (parts[1] || "")
      : parts.slice(1).join(" ");
    const [ipAddress, subnet = ""] = ipWithSubnet.split("/");

    if (!isValidIpV4(ipAddress)) continue;
    if (!/^\d{1,2}$/.test(subnet)) continue;

    const subnetNumber = Number(subnet);
    if (subnetNumber < 0 || subnetNumber > 32) continue;

    result.push({
      ipAddress,
      subnet,
      description,
    });

  }

  return result;
};

const ImportView = ({
  form,
  onClose,
  onUpload,
}) => {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const openFileBrowser = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    e.target.value = "";
  };

  const handleUpload = () => {
    if (!file || isProcessing) return;

    const reader = new FileReader();
    reader.onload = () => {
      setIsProcessing(true);
      // Yield once so the loading overlay paints before parsing the file.
      setTimeout(() => {
        const rows = parseIpCsv(reader.result || "");
        onUpload(rows);
        setIsProcessing(false);
        onClose();
      }, 0);
    };
    reader.readAsText(file);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          Import IP Address List
        </Typography>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            width: "80%",
            gap: "20px",
            marginTop: "40px",
          }}
        >
          <Box sx={form.getLabelStyle()}>Choose File:</Box>
          <Box style={{ maxWidth: "100%" }}>
            <TextField
              variant="outlined"
              size="small"
              value={file ? file.name : ""}
              placeholder="Select a file"
              disabled
              sx={{ ...form.getTextfieldStyle(true), width: "100%" }}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
          <OutlineRectangleButton
            startIcon={<AddIcon />}
            height="32px"
            onClick={openFileBrowser}
            disabled={isProcessing}
          >
            Browse
          </OutlineRectangleButton>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={onClose} sx={{ marginRight: "20px" }} width="100px" height="32px" disabled={isProcessing}>
          Cancel
        </OutlineButton>
        <SolidButton onClick={handleUpload} width="100px" height="32px" disabled={!file || isProcessing}>
          Upload
        </SolidButton>
      </Box>
      {isProcessing && <Loading />}
    </Box>
  );
};

export default React.memo(ImportView);
